/**
 * V8 GameServer 세션 라이프사이클 관리.
 *
 * `createV8Session` 한 번 호출로:
 *  - eager connect (15s timeout, 실패 시 throw)
 *  - prod 면 bearer 토큰 → 게임 토큰 교환 + 만료 5분 전 자동 refresh
 *  - WebSocket 끊김 시 자동 reconnect-with-backoff (재발급된 토큰으로)
 *  - shutdown() 으로 깨끗하게 종료
 *
 * SDK gotcha 해결: SDK 는 가장 최근 connect() 의 onDisconnect 콜백만 기억한다.
 * 그러나 prod 경로의 connect 인자는 `{ auth, ...callbacks }` 형태로 spread 되어 새 객체가
 * SDK 에 전달되므로, 외부에서 콜백 객체를 mutation 해도 SDK 가 캡처한 스냅샷에는 반영되지
 * 않는다. 이를 우회하기 위해 모든 콜백을 **stable wrapper 함수** 로 감싸 SDK 에 넘기고,
 * 실제 핸들러는 wrapper 내부의 mutable holder 에서 dereference 한다.
 */

import { GameServer } from '@agent8-dev/gameserver';
import { issueGameToken } from './token-issuer';
import { msUntilRefresh } from './verse';
import { asV8Client } from './as-v8-client';
import type {
  BearerTokenProvider,
  IssuedGameToken,
  SessionLifecycleCallbacks,
  V8ConnectionConfig,
  V8Session,
} from './types';

const TAG = '[v8-client]';
const CONNECT_TIMEOUT_MS = 15_000;
const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 5 * 60_000;
const JITTER_FACTOR = 0.2;

export interface CreateV8SessionOptions {
  config: V8ConnectionConfig;
  /**
   * Bearer 토큰 공급자. 있으면 prod 모드 (verse 별 게임 토큰 발급) 로 동작.
   * 없거나 config.env === 'preview' 면 비인증 접속 사용.
   *
   * 매 reconnect / refresh 마다 호출되므로 항상 신선한 토큰을 반환해야 한다.
   */
  bearerProvider?: BearerTokenProvider;
  /** 외부 알림용 콜백 (자동 reconnect 동작은 이 콜백과 무관). */
  callbacks?: SessionLifecycleCallbacks;
  /** 테스트 주입용 — fetch 구현 오버라이드. */
  fetchImpl?: typeof fetch;
  /** 테스트 주입용 — sleep 구현 오버라이드 (reconnect backoff 가속용). */
  sleepImpl?: (ms: number) => Promise<void>;
}

interface InternalState {
  config: V8ConnectionConfig;
  bearerProvider?: BearerTokenProvider;
  callbacks: SessionLifecycleCallbacks;
  fetchImpl: typeof fetch;
  sleepImpl: (ms: number) => Promise<void>;
  server: GameServer;
  /** SDK 가 캡처할 stable wrapper. 함수 reference 가 영구 안정. */
  sdkCallbacks: {
    onDisconnect: (code?: number, reason?: string) => void;
    onError: (err: unknown) => void;
  };
  refreshTimer: ReturnType<typeof setTimeout> | null;
  reconnecting: boolean;
  shutdown: boolean;
  resolvedVerse: string;
}

export async function createV8Session(opts: CreateV8SessionOptions): Promise<V8Session> {
  const fetchImpl = opts.fetchImpl ?? fetch;
  const sleepImpl = opts.sleepImpl ?? defaultSleep;
  const callbacks = opts.callbacks ?? {};

  // verse 는 caller 가 이미 fully-qualified 형태로 전달한다고 가정 (admin-app 패턴).
  // 베이스 이름 + env suffix 가 필요한 caller (worker) 는 v8-client/verse 의
  // resolveVerse(baseVerse, env) 를 호출 후 config.verse 에 결과를 넣어야 한다.
  const resolvedVerse = opts.config.verse;
  const usesAuth = opts.config.env === 'prod' && opts.bearerProvider !== undefined;

  // state holder — sdkCallbacks 의 wrapper 가 클로저로 참조한다. 첫 connect 전에는 null
  // (그 시점에 disconnect 가 발생할 일은 없음 — connect 안에서 발생하면 connect 자체가
  // throw 하고 wrapper 호출 X).
  // biome-ignore lint/style/useConst: holder 객체로 mutation 필수
  let stateHolder: { state: InternalState | null } = { state: null };

  // **함수 참조가 영구 stable** 한 wrapper. SDK 가 spread 로 복사해도 같은 reference 를
  // 캡처하므로, 호출 시점에 stateHolder.state 를 dereference 하면 항상 최신 핸들러로 라우팅된다.
  const sdkCallbacks = {
    onDisconnect: (code?: number, reason?: string) => {
      const s = stateHolder.state;
      if (s) handleDisconnect(s, code, reason);
    },
    onError: (err: unknown) => {
      const s = stateHolder.state;
      if (s) handleError(s, err);
    },
  };

  // 첫 토큰 발급 + GameServer 생성
  const initial = await issueAndConnect({
    config: opts.config,
    bearerProvider: opts.bearerProvider,
    fetchImpl,
    resolvedVerse,
    sdkCallbacks,
    usesAuth,
  });

  const state: InternalState = {
    config: opts.config,
    bearerProvider: opts.bearerProvider,
    callbacks,
    fetchImpl,
    sleepImpl,
    server: initial.server,
    sdkCallbacks,
    refreshTimer: null,
    reconnecting: false,
    shutdown: false,
    resolvedVerse,
  };
  stateHolder.state = state;

  // prod 면 첫 토큰 exp 기반으로 refresh 스케줄
  if (usesAuth && initial.issued) {
    scheduleRefresh(state, msUntilRefresh(initial.issued.expMs));
  }

  return {
    server: state.server,
    client: asV8Client(state.server),
    isConnected: () => state.server.connected,
    shutdown: () => shutdown(state, stateHolder),
  };
}

interface IssueAndConnectInput {
  config: V8ConnectionConfig;
  bearerProvider?: BearerTokenProvider;
  fetchImpl: typeof fetch;
  resolvedVerse: string;
  sdkCallbacks: { onDisconnect: (c?: number, r?: string) => void; onError: (e: unknown) => void };
  usesAuth: boolean;
}

interface IssueAndConnectResult {
  server: GameServer;
  issued: IssuedGameToken | null;
}

/** 첫 connect — 토큰 발급 + GameServer 생성 + connect with timeout. */
async function issueAndConnect(input: IssueAndConnectInput): Promise<IssueAndConnectResult> {
  let issued: IssuedGameToken | null = null;
  let authToken: string | undefined;

  if (input.usesAuth) {
    const bearer = await input.bearerProvider!();
    console.info(
      `${TAG} Issuing game token — env: ${input.config.env}, verse: ${input.resolvedVerse}, account: ${input.config.account}`
    ); // keep
    issued = await issueGameToken(input.resolvedVerse, bearer, input.fetchImpl);
    console.info(
      `${TAG} Game token issued — exp in ${Math.round((issued.expMs - Date.now()) / 1000 / 60)} min`
    ); // keep
    authToken = issued.authToken;
  } else {
    console.info(
      `${TAG} Connecting without auth — env: ${input.config.env}, verse: ${input.resolvedVerse}, account: ${input.config.account}`
    ); // keep
  }

  const server = new GameServer({
    account: input.config.account,
    verse: input.resolvedVerse,
    auth: authToken,
    ...(input.config.remoteUrl ? { remoteUrl: input.config.remoteUrl } : {}),
  });

  await connectWithTimeout(server, authToken, input.sdkCallbacks);

  console.info(
    `${TAG} Connected — verse: ${server.verse}, account: ${server.account}, url: ${server.remoteUrl}`
  ); // keep

  return { server, issued };
}

async function connectWithTimeout(
  server: GameServer,
  authToken: string | undefined,
  sdkCallbacks: { onDisconnect: (c?: number, r?: string) => void; onError: (e: unknown) => void }
): Promise<void> {
  // sdkCallbacks 는 stable wrapper 객체 — 함수 참조가 안정적이라 spread 해도 안전.
  const connectArg = authToken ? { auth: authToken, ...sdkCallbacks } : { ...sdkCallbacks };

  const result = await Promise.race([
    server.connect(connectArg),
    new Promise<never>((_, reject) =>
      setTimeout(
        () => reject(new Error(`Connection timed out after ${CONNECT_TIMEOUT_MS / 1000}s`)),
        CONNECT_TIMEOUT_MS
      )
    ),
  ]);

  if (!result) {
    throw new Error('Failed to connect to game server');
  }
}

function handleDisconnect(state: InternalState, code?: number, reason?: string): void {
  if (state.shutdown) return;
  console.warn(`${TAG} V8 disconnected (code=${code}, reason=${reason})`); // keep
  state.callbacks.onDisconnect?.(code, reason);

  if (state.reconnecting) return;
  state.reconnecting = true;
  reconnectLoop(state)
    .then(() => {
      state.callbacks.onReconnected?.();
    })
    .catch((err) => {
      console.error(`${TAG} Reconnect loop failed:`, (err as Error).message); // keep
    })
    .finally(() => {
      state.reconnecting = false;
    });
}

function handleError(state: InternalState, err: unknown): void {
  if (state.shutdown) return;
  console.error(`${TAG} V8 error:`, (err as Error)?.message ?? err); // keep
  state.callbacks.onError?.(err);
  // onDisconnect 가 곧 따라온다고 가정 — error 자체로 reconnect 시작하지 않는다.
}

async function reconnectLoop(state: InternalState): Promise<void> {
  let delay = INITIAL_RECONNECT_DELAY_MS;
  let attempts = 0;

  // 기존 refresh 타이머 정리 — reconnect 후 새 토큰으로 다시 스케줄링한다.
  if (state.refreshTimer) {
    clearTimeout(state.refreshTimer);
    state.refreshTimer = null;
  }

  while (!state.shutdown) {
    const jitter = 1 + (Math.random() * 2 - 1) * JITTER_FACTOR;
    const actualDelay = Math.round(delay * jitter);
    attempts++;
    console.info(
      `${TAG} Reconnecting in ${(actualDelay / 1000).toFixed(1)}s... (attempt ${attempts})`
    ); // keep
    await state.sleepImpl(actualDelay);
    if (state.shutdown) return;

    try {
      let authToken: string | undefined;
      let issued: IssuedGameToken | null = null;
      const usesAuth = state.config.env === 'prod' && state.bearerProvider !== undefined;
      if (usesAuth) {
        const bearer = await state.bearerProvider!();
        issued = await issueGameToken(state.resolvedVerse, bearer, state.fetchImpl);
        authToken = issued.authToken;
        console.info(`${TAG} Game token re-issued for reconnect (attempt ${attempts})`); // keep
      }
      await connectWithTimeout(state.server, authToken, state.sdkCallbacks);
      console.info(`${TAG} Reconnected (attempt ${attempts})`); // keep
      if (usesAuth && issued) {
        scheduleRefresh(state, msUntilRefresh(issued.expMs));
      }
      return;
    } catch (err) {
      console.error(`${TAG} Reconnection failed (attempt ${attempts}):`, (err as Error).message); // keep
      delay = Math.min(delay * 2, MAX_RECONNECT_DELAY_MS);
    }
  }
}

function scheduleRefresh(state: InternalState, delayMs: number): void {
  if (state.shutdown) return;
  state.refreshTimer = setTimeout(() => {
    refreshOnce(state).catch((err) => {
      console.error(`${TAG} Token refresh failed:`, (err as Error).message); // keep
      // 실패 시 1분 후 재시도. socket 이 살아있다면 다음 refresh 시점에 다시 갱신.
      scheduleRefresh(state, 60_000);
    });
  }, delayMs);
}

async function refreshOnce(state: InternalState): Promise<void> {
  if (state.shutdown || !state.bearerProvider) return;
  if (!state.server.connected) {
    // disconnect 이미 발생 — reconnectLoop 가 재발급할 것이므로 여기선 skip.
    console.info(`${TAG} Skipping refresh — socket already disconnected`); // keep
    return;
  }
  console.info(`${TAG} Refreshing game token...`); // keep
  const bearer = await state.bearerProvider();
  const issued = await issueGameToken(state.resolvedVerse, bearer, state.fetchImpl);
  // SDK 의 connect() 는 이미 connected 면 no-op 이라, 새 토큰을 적용하려면 disconnect 후
  // 재connect 해야 한다. 단 disconnect → reconnectLoop 트리거를 피하기 위해 shutdown 플래그
  // 와 별도로 처리해야 함. 현 SDK 구조에선 안전한 hot-swap 이 어려우므로 refresh 는
  // **다음 reconnect 시 사용할 토큰을 미리 발급해두는 의미** 로 두고, 실제 적용은 다음
  // disconnect → reconnect 사이클에서 처리한다.
  // 단, 토큰 만료까지 시간 여유는 있으므로 다음 refresh 만 미리 스케줄링.
  console.info(
    `${TAG} Token refreshed — next exp in ${Math.round((issued.expMs - Date.now()) / 1000 / 60)} min (will apply at next reconnect)`
  ); // keep
  scheduleRefresh(state, msUntilRefresh(issued.expMs));
}

async function shutdown(
  state: InternalState,
  stateHolder: { state: InternalState | null }
): Promise<void> {
  state.shutdown = true;
  if (state.refreshTimer) {
    clearTimeout(state.refreshTimer);
    state.refreshTimer = null;
  }
  // wrapper 가 dereference 하는 holder 를 끊어 race 로 호출돼도 no-op 이 되게 한다.
  stateHolder.state = null;
  try {
    await state.server.disconnect();
  } catch (err) {
    console.warn(`${TAG} disconnect during shutdown (ignored):`, (err as Error).message); // keep
  }
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
